import PropTypes from 'prop-types';
import Modal from 'react-modal';
import {Translate} from 'react-redux-i18n';
import {AbstractFormComponent} from 'front-onceforall-core';
import {TextField} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import {requiredValidator} from 'front-onceforall-core/dist/utils/validators';
import {REJECTION_REASONS} from '../../utils/constant';
import interventionService from '../../services/intervention/InterventionService';
import relationshipService from '../../services/relationship/RelationshipService';

import CancelConfirmButtons from '../buttons/form/CancelConfirmButtons';
import SuggestedOrganisationItem from '../SuggestedRelationships/SuggestedOrganisationItem';

import {extractInformationFromNetworkRelationship} from './utils';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(255,255,255,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2
  },
  content: {
    backgroundColor: '#fff',
    borderRadius: 4,
    boxShadow: 'rgba(0, 0, 0, 0.35) 0px 0px 32px 0px',
    minWidth: 450,
    outline: 0,
    padding: 20,
    position: 'relative'
  }
};

class InvitationRejectionReasonForm extends AbstractFormComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      invitation: {}
    };
  }

  componentDidMount() {
    this.setInvitationData();
  }

  getFormData() {
    return {
      rejectionReason: this.props.isSuggestion ? REJECTION_REASONS.UNKNOWN_CLIENT : REJECTION_REASONS.UNKNOWN_ORGANISATION
    };
  }

  getFormValidators() {
    return {
      rejectionReason: requiredValidator
    };
  }

  handleSuggestionCallback() {
    const {invitation, formData} = this.state;
    this.props.rejectSuggection(invitation.invitationId, formData.rejectionReason)
      .then(() => {
        this.props.onSubmitSuccess();
      })
      .catch(() => {
        this.props.onSubmitError();
      })
      .finally(() => {
        this.props.onClose();
      });
  }

  handleSubmitCallback() {
    if (this.props.isSuggestion) {
      this.handleSuggestionCallback();
    } else {
      let service = interventionService;
      if (this.props.invitation.relationshipId || this.props.invitation.relationship) {
        service = relationshipService;
      }
      const {invitation, formData} = this.state;
      service.reject(invitation.invitationId, formData.rejectionReason)
        .then(response => {
          this.props.onSubmitSuccess(response);
          this.props.onClose();
        })
        .catch(() => {
          this.props.onSubmitError();
          this.props.onClose();
        });
    }
  }

  setInvitationData = async () => {
    if (this.props.invitation.relationship && this.props.invitation.isFromQuery) {
      // rejected relationship from url
      relationshipService.getRelationship(this.props.invitation.relationship.relationshipId)
        .then(relationship => {
          this.setState({
            loading: false,
            invitation: extractInformationFromNetworkRelationship(relationship)
          });
        });
    } else if (this.props.invitation.intervention && this.props.invitation.isFromQuery) {
      // rejected project from url
      interventionService.getIntervention(this.props.invitation.intervention.interventionId)
        .then(intervention => {
          this.setState({
            loading: false,
            invitation: {
              invitingOrganisationName: intervention.invitationEmitterOrganisation.fullName,
              invitationId: this.props.invitation.intervention.interventionId
            }
          });
        });
    } else if (this.props.invitation.intervention) {
      // rejected project from Dashboard view
      this.setState({
        loading: false,
        invitation: {
          invitingOrganisationName: this.props.invitation.intervention.invitingOrganisationName,
          invitationId: this.props.invitation.intervention.interventionId
        }
      });
    } else if (this.props.invitation.relationship) {
      // rejected relationship from Dashboard view
      // invited as subcontractor
      this.setState({
        loading: false,
        invitation: {
          invitingOrganisationName: this.props.invitation.relationship.invitingOrganisationName,
          invitationId: this.props.invitation.relationship.relationshipId
        }
      });
    } else if (this.props.invitation.invitationId) {
      // rejected suggestion
      this.setState({
        loading: false,
        invitation: {...this.props.invitation}
      });
    } else {
      // rejected relationship from MyClient view
      this.setState({
        loading: false,
        invitation: {
          invitingOrganisationName: this.props.invitation.organisationSource.fullName,
          invitationId: this.props.invitation.relationshipId
        }
      });
    }
  };

  displayMenuItem(reason) {
    return (
      <MenuItem
        key={REJECTION_REASONS[reason]}
        value={REJECTION_REASONS[reason]}
      >
        <Translate value={`rejectionReason.reason.${reason}`}/>
      </MenuItem>
    );
  }

  renderForm = () => {
    const {isSuggestion} = this.props;
    const possibleReasons = isSuggestion ?
      [REJECTION_REASONS.UNKNOWN_CLIENT, REJECTION_REASONS.WORKS_WITH_ANOTHER_ORGANISATION, REJECTION_REASONS.NO_ACTIVE_CONTRACT] :
      [REJECTION_REASONS.UNKNOWN_ORGANISATION, REJECTION_REASONS.NOT_WORKING_WITH_ORGANISATION, REJECTION_REASONS.NO_LONGER_WORKING_WITH_ORGANISATION];
    return (
      <form
        className="form-layout"
        onSubmit={this.handleSubmit}
      >
        <h3><Translate
          value={isSuggestion ? 'invitation.suggestion.reject' : 'rejectionReason.title'}
          invitingOrganisationName={this.state.invitation?.invitingOrganisationName}
        /></h3>
        {isSuggestion && <SuggestedOrganisationItem suggestion={this.state.invitation}/>}
        <div>
          <div className="py-3 information">
            <Translate value={isSuggestion ? 'invitation.suggestion.rejectionReason' : 'rejectionReason.label'}/>
          </div>
          <TextField
            select
            name="rejectionReason"
            id="rejectionReason"
            value={this.state.formData.rejectionReason}
            onChange={this.handleChange('rejectionReason')}
          >
            {possibleReasons.map(e => this.displayMenuItem(e))}
          </TextField>
        </div>
        <CancelConfirmButtons
          onClose={this.props.onClose}
          submitting={this.state.submitting}
        />
      </form>
    );
  };

  render() {
    return (
      <Modal
        defaultStyles={modalStyles}
        isOpen
        onRequestClose={this.props.onClose}
        ariaHideApp={false}
        shouldCloseOnOverlayClick
      >
        {this.state.loading ?
          <div className="loading-overlay"/> :
          this.renderForm()
        }
      </Modal>
    );
  }
}

InvitationRejectionReasonForm.defaultProps = {
  onSubmitError: () => {},
  isSuggestion: false
};

InvitationRejectionReasonForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  invitation: PropTypes.object.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onSubmitError: PropTypes.func,
  rejectSuggection: PropTypes.func,
  isSuggestion: PropTypes.bool
};

export default InvitationRejectionReasonForm;
